import { useEffect, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import OwnerRequests from "@/pages/owner/OwnerRequests";
import OwnerContractors from "@/pages/owner/OwnerContractors";
import OwnerApplications from "@/pages/owner/OwnerApplications";
import OwnerBuilds from "@/pages/owner/OwnerBuilds";
import OwnerSettings from "@/pages/owner/OwnerSettings";
import OwnerTeam from "@/pages/owner/OwnerTeam";
import OwnerAnalytics from "@/pages/owner/OwnerAnalytics";

function Stats() {
  const { t } = useI18n();
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/owner/stats").then((r) => setS(r.data)); }, []);
  if (!s) return null;
  const open = Object.entries(s.by_status).filter(([k]) => !["closed", "customer_canceled", "unable_to_serve"].includes(k)).reduce((a, [, v]) => a + v, 0);
  const items = [[t("o.stat_open"), open, "stat-open"], [t("o.stat_urgent"), s.urgent_open, "stat-urgent"], [t("o.stat_est"), s.estimates_to_review, "stat-estimates"],
    [t("o.stat_apps"), s.applications_new, "stat-applications"], [t("o.stat_pros"), s.contractors_active, "stat-contractors"]];
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-6">
      {items.map(([l, v, id], i) => (
        <div key={id} className={`rounded-2xl p-4 border ${i === 1 && v > 0 ? "bg-red-50 border-red-200" : "bg-white border-slate-200"}`} data-testid={id}>
          <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{l}</p>
          <p className="font-mono text-3xl font-semibold mt-1">{v}</p>
        </div>
      ))}
    </div>
  );
}

export default function OwnerDashboard() {
  const { t } = useI18n();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "requests";
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="owner-dashboard">
      <p className="text-xs font-semibold uppercase tracking-widest text-teal-700">{t("o.console")}</p>
      <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">{t("o.ops")}</h1>
      <Stats key={tab} />
      <Tabs value={tab} onValueChange={(v) => setSp({ tab: v })} className="mt-8">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="requests" data-testid="owner-tab-requests">{t("o.tab_requests")}</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="owner-tab-analytics">{t("o.tab_analytics")}</TabsTrigger>
          <TabsTrigger value="contractors" data-testid="owner-tab-contractors">{t("o.tab_contractors")}</TabsTrigger>
          <TabsTrigger value="applications" data-testid="owner-tab-applications">{t("o.tab_apps")}</TabsTrigger>
          <TabsTrigger value="builds" data-testid="owner-tab-builds">{t("o.tab_builds")}</TabsTrigger>
          <TabsTrigger value="settings" data-testid="owner-tab-settings">{t("o.tab_settings")}</TabsTrigger>
          <TabsTrigger value="team" data-testid="owner-tab-team">{t("o.tab_team")}</TabsTrigger>
        </TabsList>
        <TabsContent value="requests" className="mt-6"><OwnerRequests /></TabsContent>
        <TabsContent value="analytics" className="mt-6"><OwnerAnalytics /></TabsContent>
        <TabsContent value="contractors" className="mt-6"><OwnerContractors /></TabsContent>
        <TabsContent value="applications" className="mt-6"><OwnerApplications /></TabsContent>
        <TabsContent value="builds" className="mt-6"><OwnerBuilds /></TabsContent>
        <TabsContent value="settings" className="mt-6"><OwnerSettings /></TabsContent>
        <TabsContent value="team" className="mt-6"><OwnerTeam /></TabsContent>
      </Tabs>
    </div>
  );
}
